/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild
} from '@angular/core';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {of} from 'rxjs';
import {debounceTime, finalize, map, switchMap, switchMapTo, takeUntil, tap} from 'rxjs/operators';

import {AbsencesByFamilyChart} from './charts/absences-family.chart';
import {ContractTypesChart} from './charts/contract-types.chart';
import {ContractsToCheckChart} from './charts/contracts-to-check.chart';
import {FTEandHeadcountChart} from './charts/fte-headcount.chart';
import {HiredTerminationChart} from './charts/hired-termination.chart';
import {PayrollStatusChart} from './charts/payroll-status.chart';
import {PayrollSyncStatusChart} from './charts/payroll-sync-status.chart';
import {TerminationReasonChart} from './charts/termination-reason.chart';
import {TurnOverChart} from './charts/turn-over.chart';
import {ValidationPlanFamilyChart} from './charts/validation-plan-family.chart';
import {ValidationPlanTypeChart} from './charts/validation-plan-type.chart';
import {DashboardBox, ResponseChartBox} from './dashboard.interfaces';
import {DashboardService} from './dashboard.service';

import {PayTypeSelector} from '../+payrolls/payrolls.model';
import {ChartAbstract} from '../+postpayroll/check/key-metrics/charts/chart';
import {ChartUtils} from '../+postpayroll/check/key-metrics/charts/chart.utils';
import {Chart} from '../+postpayroll/check/key-metrics/key-metrics.interfaces';
import {RESIZE_EVENT} from '../app.constants';
import {GrossSalaryChart} from '../dashboard/charts/gross-salary.chart';
import {Destroyable} from '../destroyable.component';
import {CommunicationsService} from '../inbox/shared/communications.service';
import {Datum} from '../shared/charts/echarts/echarts.component';
import {EmployeesContextUtilsService} from '../shared/employees/utils/employee-context-utils.service';
import {LocalizationService} from '../shared/localization/localization.service';
import {UserService} from '../shared/user/user.service';

@Component({
  selector: 'gp-view-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DashboardComponent extends Destroyable implements OnInit {

  public static ACCESS_NAME: string = 'Dashboard';

  @ViewChild('widgetsContainer', {static: true})
  public widgetsContainer: ElementRef;

  public boxes: DashboardBox[];
  public isAllClosed: boolean = false;

  public isAllOpened: boolean = false;
  public isEditing: boolean = false;
  public isEditPanelLoaded: boolean = true;

  private dragEl: HTMLElement;
  private dragBox: DashboardBox;

  public selectedRegulation = '';
  public currency = '';

  constructor(private userService: UserService,
              private translate: TranslateService,
              private dashboardService: DashboardService,
              private localization: LocalizationService,
              private renderer: Renderer2,
              private router: Router,
              private cd: ChangeDetectorRef,
              private communicationsService: CommunicationsService,
              private employeesContextUtilsService: EmployeesContextUtilsService) {
    super();
  }

  public ngOnInit(): void {
    this.communicationsService.notificationUpdated
      .pipe(
        takeUntil(this.componentDestroyed$)
      )
      .subscribe(() => {
        this.cd.markForCheck();
      });

    this.userService.getUser()
      .pipe(
        takeUntil(this.componentDestroyed$),
        debounceTime(400),
        switchMapTo(this.dashboardService.getDashboardConfig()),
        tap((cardsConfig: any) => {
          this.fetchAllCharts(cardsConfig);
          this.sortBoxes();

          if (this.isEditing) {
            this.toggleEditPanel();
          }
        })
      )
      .subscribe();

    this.employeesContextUtilsService.listenContextPayType()
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        if (this.boxes) {
          for (const box of this.boxes) {
            this.refreshChart(box);
          }
        }
      });
  }

  // --------------------------------------------------DnD--------------------------------------------------------------

  public onDragStart(e, box): void {
    const dragElem: HTMLElement = e.target.closest('.draggable');
    this.dragEl = dragElem.parentElement;
    this.dragBox = box;

    if (!this.dragBox.isClosed) {
      this.renderer.setStyle(this.dragEl, 'opacity', '0.3');
    }
  }

  public onWidgetBoardDrag(): void {

    if (!this.dragBox.isClosed) {
      return;
    }

    if (this.isAllClosed) {
      this.isAllClosed = false;
    }

    this.openBox();
    this.renderer.appendChild(this.widgetsContainer.nativeElement, this.dragEl);
  }

  public onDragEnter(e): void {
    if (this.dragBox.isClosed) {
      this.openBox();
      this.onDragEnter(e);

      return;
    }

    const onDragElem: HTMLElement = e.target.closest('.on-drag');
    const clientRect: ClientRect = onDragElem.getBoundingClientRect();
    const dragClientRect: ClientRect = this.dragEl.getBoundingClientRect();

    if (dragClientRect.top < clientRect.top) {
      this.renderer.insertBefore(this.widgetsContainer.nativeElement, this.dragEl, onDragElem.nextSibling);
    } else if (dragClientRect.top > clientRect.top) {
      this.renderer.insertBefore(this.widgetsContainer.nativeElement, this.dragEl, onDragElem);
    } else if (dragClientRect.right < clientRect.right) {
      this.renderer.insertBefore(this.widgetsContainer.nativeElement, this.dragEl, onDragElem.nextSibling);
    } else {
      this.renderer.insertBefore(this.widgetsContainer.nativeElement, this.dragEl, onDragElem);
    }
  }

  public openBox(): void {
    this.dragBox.isClosed = false;
    this.refreshChart(this.dragBox);

    const children: HTMLElement[] = Array.from(this.widgetsContainer.nativeElement.children);
    this.dragEl = children.find((child: HTMLElement) =>
      child.getAttribute('data-card-type') === this.dragBox.cardType);
    this.renderer.setStyle(this.dragEl, 'opacity', '0.3');
  }

  public onDragEnd(): void {
    this.renderer.removeStyle(this.dragEl, 'opacity');
    this.dragEl = null;
    this.dragBox = null;
    this.updateChartConfig();
  }

  public onDrag(e: MouseEvent): void {
    if (e.clientY < 100) {
      const top: number = window.pageYOffset || document.documentElement.scrollTop;
      window.scrollTo(null, top - 5);
    }
  }

  // -------------------------------------------------------------------------------------------------------------------
  public deleteCard(box: DashboardBox): void {
    box.isClosed = true;
    this.updateChart(box);
    setTimeout(() => {
      this.updateChartConfig();
    });
  }

  public updateAllOpenedStatus(): void {
    this.isAllOpened = true;
    this.isAllClosed = true;
    this.boxes.forEach((box: DashboardBox) => {

      if (box.isClosed) {
        this.isAllOpened = false;
      }

      if (!box.isClosed) {
        this.isAllClosed = false;
      }

    });
    this.cd.markForCheck();
  }

  public updateChartConfig(): void {
    const data = {};
    Array.from(this.widgetsContainer.nativeElement.children).forEach((child: HTMLElement, index) => {
      const type: string = child.getAttribute('data-card-type');
      if (type && child.getAttribute('data-hidden') === 'false') {
        data[type] = Object.keys(data).length;
      }
    });
    this.dashboardService.updateChartConfigs(data).subscribe();
    this.updateAllOpenedStatus();
  }

  public refreshChart(chartBox: DashboardBox): void {
    this.updateChart(chartBox, true);
  }

  public onRegulationChange(event: any, chartBox: DashboardBox): void {
    chartBox.selectedRegulation = event.target.value;
    this.updateChart(chartBox);
  }

  public getRegulationsForChart(chartBox: DashboardBox): string[] {
    if (chartBox.params) {
      return [...Object.keys(chartBox.params)];
    }
    return [];
  }

  public updateChart(chartBox: DashboardBox, isRefresh: boolean = false): void {
    let cb: (chartBox: DashboardBox) => Chart;

    switch (chartBox.cardType) {
      case 'HIRE_TERMINATION':
        chartBox.title = 'DASHBOARD.hired_termination';
        chartBox.description = 'DASHBOARD.INFO.hire_and_termination';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(HiredTerminationChart, dashboardBox);
        break;
      case 'TURNOVER':
        chartBox.title = 'DASHBOARD.turnover';
        chartBox.description = 'DASHBOARD.INFO.turnover';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(TurnOverChart, dashboardBox);
        break;
      case 'CONTRACT_TYPES':
        chartBox.title = 'DASHBOARD.contract_types';
        chartBox.description = 'DASHBOARD.INFO.employees_per_contract_type';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(ContractTypesChart, dashboardBox);
        break;
      case 'TERMINATION_REASONS':
        chartBox.title = 'EMPLOYEE_LEAVE.termination_reason';
        chartBox.description = 'DASHBOARD.INFO.termination_reason';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(TerminationReasonChart, dashboardBox);
        break;
      case 'FTE_AND_HEADCOUNT':
        chartBox.title = 'DASHBOARD.FTE_and_head_count';
        chartBox.description = 'DASHBOARD.INFO.fte_and_headcount';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(FTEandHeadcountChart, dashboardBox, true);
        break;
      case 'ABSENCES_BY_FAMILY':
        chartBox.title = 'DASHBOARD.absences_family';
        chartBox.description = 'DASHBOARD.INFO.absences_by_family';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(AbsencesByFamilyChart, dashboardBox);
        break;
      case 'PAYROLL_STATUS':
        chartBox.title = 'DASHBOARD.payroll_status';
        chartBox.description = 'DASHBOARD.INFO.payroll_status';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(PayrollStatusChart, dashboardBox);
        break;
      case 'VALIDATION_PLAN_BY_TYPE':
        chartBox.title = 'DASHBOARD.validation_plan_by_type';
        chartBox.description = 'DASHBOARD.INFO.validation_plan_by_type';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(ValidationPlanTypeChart, dashboardBox);
        break;
      case 'VALIDATION_PLAN_BY_FAMILY':
        chartBox.title = 'DASHBOARD.validation_plan_by_family';
        chartBox.description = 'DASHBOARD.INFO.validation_plan_by_family';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(ValidationPlanFamilyChart, dashboardBox);
        break;
      case 'CONTRACTS_TO_CHECK':
        chartBox.title = 'DASHBOARD.contracts_to_check';
        chartBox.description = 'DASHBOARD.INFO.contracts_to_check';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(ContractsToCheckChart, dashboardBox);
        break;
      case 'PAYROLL_VALIDATION_PROGRESS':
        chartBox.title = 'DASHBOARD.payroll_validation_progress';
        chartBox.description = 'DASHBOARD.INFO.payroll_validation_progress';
        cb = (dashboardBox: DashboardBox) => {
          dashboardBox.progress = dashboardBox.params[Object.keys(dashboardBox.params)[0]];
          delete dashboardBox.chart;
          return null;
        };
        break;
      case 'VACANT_POSITIONS':
        chartBox.title = 'DASHBOARD.vacant_positions';
        chartBox.description = 'DASHBOARD.INFO.vacant_positions';
        cb = (dashboardBox: DashboardBox) => {
          dashboardBox.position = dashboardBox.params[Object.keys(dashboardBox.params)[0]];
          delete dashboardBox.chart;
          return null;
        };
        break;
      case 'NOTIFICATION':
        chartBox.title = 'DASHBOARD.notifications';
        chartBox.description = 'DASHBOARD.INFO.notifications';
        chartBox.notification = true;
        return;
      case 'PAYROLL_SYNC_STATUS':
      case DashboardService.CARD_FALLBACKS['PAYROLL_SYNC_STATUS']:
        chartBox.title = 'DASHBOARD.payroll_data_sync_v2_status';
        chartBox.description = 'DASHBOARD.INFO.payroll_data_sync_status';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(PayrollSyncStatusChart, dashboardBox);
        break;
      case 'SDC3_SYNC_STATUS':
      case DashboardService.CARD_FALLBACKS['SDC3_SYNC_STATUS']:
        chartBox.title = 'DASHBOARD.payroll_data_sync_v3_status';
        chartBox.description = 'DASHBOARD.INFO.payroll_data_sync_status';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(PayrollSyncStatusChart, dashboardBox);
        break;
      case 'GROSS_SALARY_PER_REGULATION':
        chartBox.title = 'DASHBOARD.gross_salary_per_regulation';
        chartBox.description = 'DASHBOARD.INFO.gross_salary_per_regulation';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(GrossSalaryChart, dashboardBox);
        break;
      case 'PAYROLL_INPUT_SYNC_STATUS' :
        chartBox.title = 'DASHBOARD.payroll_input_sync_status';
        chartBox.description = 'DASHBOARD.INFO.payroll_input_sync_status';
        cb = (dashboardBox: DashboardBox) => this.getDefaultChart(PayrollSyncStatusChart, dashboardBox);
        break;
      default:
        chartBox.chart = ChartUtils.getEmptyChart();
        return;
    }

    this.updateChartData(chartBox, cb, isRefresh);
  }

  public updateChartData(chartBox: DashboardBox,
                         chartFunc: (chartBox: DashboardBox) => Chart,
                         isRefresh: boolean = false): void {
    chartBox.isLoading = true;
    let payType: string = null;
    const payTypeSelector: PayTypeSelector = this.employeesContextUtilsService.contextPayType$.getValue();
    if (payTypeSelector !== null && typeof payTypeSelector !== 'string') {
      payType = payTypeSelector.name;
    }

    this.dashboardService.getChart(chartBox.cardType, chartBox.indexNumber, isRefresh, chartBox.isClosed, payType)
      .pipe(
        switchMap((responseChartBox: ResponseChartBox) => {
          if (chartBox.cardType === 'GROSS_SALARY_PER_REGULATION') {
            const selectedRegulation = chartBox.selectedRegulation || Object.keys(responseChartBox.params)[0];
            const totalMonthlySalary = responseChartBox.params[selectedRegulation]
              .filter(item => item.name === this.getCurrentMonth())[0]?.value['TotalMonthlySalary'] || 0;
            const cliRegId = responseChartBox.params[selectedRegulation]
              .filter(item => item.name === this.getCurrentMonth())[0]?.value['CliRegId'];

            return this.dashboardService.getCurrencyByCliReg(cliRegId).pipe(
              tap((currency: string) => {
                this.currency = currency;
                const filteredDummyGrossSalaryData = {
                  'params': responseChartBox.params,
                  'currency': currency,
                  'currentMonthGrossSalary': this.localization.localizeNumber(totalMonthlySalary, 'CURRENCY_2_DIGIT', null, currency)
                };
                Object.assign(chartBox, {
                  ...responseChartBox,
                  ...filteredDummyGrossSalaryData,
                });
                chartBox.chart = chartFunc(chartBox);
              })
            );
          } else {
            Object.assign(chartBox, responseChartBox);
            chartBox.chart = responseChartBox ? chartFunc(chartBox) : ChartUtils.getEmptyChart();
            return of(null);
          }
        }),
        finalize(() => {
          chartBox.isLoading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe();
  }

  public sortBoxes(): void {
    this.boxes.sort((box1: DashboardBox, box2: DashboardBox) => box1.indexNumber - box2.indexNumber);
  }

  public getDefaultChart(chart: any, chartBox: ResponseChartBox, isAvg: boolean = false): Chart {
    let eChart: ChartAbstract;
    eChart = new chart(chartBox, this.translate, this.localization, isAvg);
    eChart.createChart();
    return eChart.chart;
  }

  public toggleEditPanel(): void {
    this.isEditPanelLoaded = true;
    this.isEditing = !this.isEditing;

    if (this.isEditing) {
      this.dashboardService.getChartList()
        .pipe(
          map((cards: { [key: string]: number }) => {
            Object.keys(DashboardService.CARD_FALLBACKS).forEach((key: string) => {
              const fallback = DashboardService.CARD_FALLBACKS[key];
              if (fallback && cards[key]) {
                cards[fallback] = cards[key];
                delete cards[key];
              }
            });
            return cards;
          }),
          finalize(() => {
            this.isEditPanelLoaded = false;
            this.updateAllOpenedStatus();
          })
        )
        .subscribe((cards: { [key: string]: number }) => {
          Object.keys(cards)
            .forEach((key: string) => {
              const foundBox = this.boxes.find((box: DashboardBox) => box.cardType === key);

              if (!foundBox) {
                const chartBox: DashboardBox = this.getEmptyBox({cardType: key});
                this.boxes.push(chartBox);
                this.updateChart(chartBox);
              }
            });
        });
    }

    window.dispatchEvent(RESIZE_EVENT);
  }

  public onChartClick(data: Datum, title: string): void {
    if (data.metadata && data.metadata.path) {
      data.metadata.queryParams.title = title;
      this.router.navigate([data.metadata.path], {
        queryParams: data.metadata.queryParams
      });
    }
  }

  private fetchAllCharts(cardsConfig: { [key: string]: number }): void {
    this.boxes = [];
    Object.keys(cardsConfig)
      .forEach((key: string) => {
        const chartBox: DashboardBox = this.getEmptyBox({
          cardType: key,
          indexNumber: cardsConfig[key],
          isClosed: false
        });
        this.boxes.push(chartBox);
        this.updateChart(chartBox);
      });
    this.updateAllOpenedStatus();
  }

  private getEmptyBox(boxConfigs?: { [key: string]: any }): DashboardBox {
    return Object.assign({
      cardType: '',
      indexNumber: 0,
      params: null,
      title: '',
      isClosed: true,
      selectedRegulation: ''
    }, boxConfigs);
  }

  private getCurrentMonth(): string {
    const now = new Date();
    const month = now.toLocaleString('en-US', {month: 'long'}).toUpperCase();
    const year = now.getFullYear();
    return `${month}-${year}`;
  }
}
